import { Num, T, useGT } from 'gt-react';

import { VirtualizedTable } from '@/components/xlsx-viewer/VirtualizedTable';
import type { SkippedRow } from './resultsToUpload';
import { skippedRowsSheet } from './skippedSheet';

/** Height of the grid; it scrolls inside this. */
const VIEWER_HEIGHT = 240;

interface Props {
  headers: string[];
  skipped: SkippedRow[];
}

/**
 * The rows that will not be uploaded, in a spreadsheet grid, exactly as they
 * appear in the supplier's workbook, each with why it was left out.
 */
export function SkippedRowsViewer({ headers, skipped }: Props) {
  const gt = useGT();
  if (skipped.length === 0) return null;
  const ctx = { context: 'why a row was left out of the upload' };
  const sheet = skippedRowsSheet(headers, skipped, {
    rowNumber: gt('Excel row', {
      context: 'first column of the skipped rows grid: the row in the workbook',
    }),
    reason: gt('Why skipped', {
      context: 'column of the skipped rows grid giving the reason',
    }),
    describe: (reason) => {
      switch (reason.kind) {
        case 'zero':
          return gt('{column} is 0', { column: reason.header, ...ctx });
        case 'blank':
          return gt('{column} is blank', { column: reason.header, ...ctx });
        case 'none-filled':
          return gt('None filled: {columns}', {
            columns: reason.headers.join(', '),
            ...ctx,
          });
      }
    },
  });
  return (
    <div className="flex flex-col gap-1">
      <h3 className="text-sm font-medium">
        <T context="heading over the grid of rows left out of the upload">
          Skipped rows (<Num>{skipped.length}</Num>) — not in the upload
        </T>
      </h3>
      <p className="text-muted-foreground text-xs">
        <T context="explanation over the skipped rows grid">
          The “Why skipped” column gives each row’s reason; fix it in the
          workbook to include the row.
        </T>
      </p>
      <div className="overflow-hidden rounded-md border">
        <VirtualizedTable sheet={sheet} height={VIEWER_HEIGHT} />
      </div>
    </div>
  );
}
